const RELOAD_KEY = 'ordo:chunk-reloads:v2'
const RELOAD_COOLDOWN_MS = 60_000

/** One recovery per loaded build, with a cooldown across rollout replicas. */
export function claimChunkRecovery(args: {
  storage: Pick<Storage, 'getItem' | 'setItem'>
  build: string | null
  now: number
  online: boolean
  editing: boolean
}): boolean {
  if (!args.build || !args.online || args.editing) {
    return false
  }
  try {
    const raw = args.storage.getItem(RELOAD_KEY)
    const saved = raw ? (JSON.parse(raw) as { builds?: unknown; at?: unknown }) : null
    const builds = Array.isArray(saved?.builds)
      ? saved.builds.filter((item): item is string => typeof item === 'string')
      : []
    if (builds.includes(args.build)) {
      return false
    }
    if (typeof saved?.at === 'number' && args.now - saved.at < RELOAD_COOLDOWN_MS) {
      return false
    }
    args.storage.setItem(RELOAD_KEY, JSON.stringify({ builds: [...builds.slice(-19), args.build], at: args.now }))
    return true
  } catch {
    // A blocked/corrupt store cannot guarantee a bounded reload.
    return false
  }
}

/** A reload cannot restore a partly typed form or a cross-origin card field. */
export function hasPendingFormInput(document: Document): boolean {
  if (document.querySelector('iframe[name^="__privateStripeFrame"], [contenteditable="true"]')) {
    return true
  }
  const focused = document.activeElement
  if (focused?.matches('input, textarea, select, [contenteditable="true"]')) {
    return true
  }
  return Array.from(document.querySelectorAll('input,textarea,select')).some((field) => {
    if (field.tagName === 'INPUT') {
      const input = field as HTMLInputElement
      if (['checkbox', 'radio'].includes(input.type)) {
        return input.checked !== input.defaultChecked
      }
      if (['hidden', 'submit', 'button'].includes(input.type)) {
        return false
      }
      return input.value.length > 0 || input.value !== input.defaultValue
    }
    if (field.tagName === 'TEXTAREA') {
      const input = field as HTMLTextAreaElement
      return input.value.length > 0 || input.value !== input.defaultValue
    }
    const select = field as HTMLSelectElement
    const options = Array.from(select.options)
    const explicitDefault = options.some((option) => option.defaultSelected)
    return options.some(
      (option, i) => option.selected !== (explicitDefault ? option.defaultSelected : !select.multiple && i === 0),
    )
  })
}

/** User edits are sticky for this document: DOM values cannot prove a save succeeded.
 * Keep manual recovery available instead of discarding a cleared or unmounted form.
 */
export function installChunkRecovery(window: Window, reload = () => window.location.reload()): () => void {
  let edited = false
  const onEdit = () => {
    edited = true
  }
  const onPreloadError = () => {
    const document = window.document
    const build = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src ?? null
    let storage: Storage
    try {
      storage = window.sessionStorage
    } catch {
      return
    }
    if (
      claimChunkRecovery({
        storage,
        build,
        now: Date.now(),
        online: window.navigator.onLine,
        editing: edited || hasPendingFormInput(document),
      })
    ) {
      reload()
    }
  }
  window.addEventListener('input', onEdit, true)
  window.addEventListener('change', onEdit, true)
  window.addEventListener('vite:preloadError', onPreloadError)
  return () => {
    window.removeEventListener('input', onEdit, true)
    window.removeEventListener('change', onEdit, true)
    window.removeEventListener('vite:preloadError', onPreloadError)
  }
}
